import { useSelector } from 'react-redux';
import Icon3D from '../Icons3D';
import { useCopilot } from '../../api/copilot';
import { useProfile } from '../../api/hooks';
import AgentStatusDot from './AgentStatusDot';
import SpeakingIndicator from './SpeakingIndicator';
import CopilotThread from './CopilotThread';
import CopilotComposer from './CopilotComposer';
import { callSafely, offersReconnect, restingText } from './util';

/**
 * Starters that work for any seat on its landing screen. The agent receives the
 * screen and its tab with every question, so "today" and "my book" need no
 * naming.
 */
const QUICK = [
  'Summarize what needs my attention today',
  'What am I looking at?',
  'What are the biggest risks right now?',
];

/**
 * Put the keyboard in the in-page assistant's field, scrolling it into view.
 *
 * The top bar's assistant button calls this on a landing screen instead of
 * opening the side column: the conversation is already on the page, so the
 * button's job there is to get the reader typing into it.
 */
export function focusAgentHome() {
  try {
    const input = document.querySelector('.agent-home .cp-input input');
    if (!input) return false;
    input.scrollIntoView({ block: 'center', behavior: 'smooth' });
    input.focus({ preventScroll: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * The copilot in the page: the first thing on a seat's landing screen, centred
 * under the greeting and above the tabs.
 *
 * The same conversation as the dock and the drawer — thread, cards and composer
 * are the shared `components/copilot` set reading one provider — so a question
 * asked here is in the side column on the next screen. The dock stands down
 * while this surface is on screen (the shell decides that by route), so the
 * reader never sees the same transcript twice.
 */
export default function AgentHome({ chips = QUICK }) {
  const {
    messages, connection = 'idle', reconnect, stalled = false,
  } = useCopilot();
  const isLoggedIn = useSelector((s) => s.auth?.isLoggedIn);
  const { user } = useProfile();
  const empty = !messages || messages.length === 0;

  /* The signed-in account's own name, never a seat's demo persona. */
  const firstName = (isLoggedIn ? String(user?.name ?? '') : '').trim().split(/\s+/)[0] || '';
  const ready = `Hi${firstName ? ` ${firstName}` : ''} — ask me about any client, fund, portfolio, risk or tax question. ` +
    'Type, press the mic to talk, or pick a starter below.';

  return (
    <section className="agent-home" aria-label="KeepInvesting assistant">
      <header className="agent-home-head">
        <div className="ai-orb ic3fi"><Icon3D name="spark" /></div>
        <b>KeepInvesting</b>
        <AgentStatusDot />
        <span className="cp-head-tools">
          <SpeakingIndicator />
          {offersReconnect(connection, stalled) && (
            <button type="button" className="cp-link-btn" onClick={() => callSafely(reconnect)}>
              Reconnect
            </button>
          )}
        </span>
      </header>

      <CopilotThread variant="inline" greeting={empty ? restingText(connection, ready) : null} />

      <div className="agent-home-foot">
        <CopilotComposer placeholder="Ask KeepInvesting anything…" chips={chips} />
      </div>
    </section>
  );
}
